import { useEffect, useMemo, useState, useRef } from "react";
import Image from "next/image";
import { Modal } from "react-bootstrap";
import { IoCloseCircle } from "react-icons/io5";
import { useCountriesQuery, Country } from "@/app/hooks/useCountries";
import { IoIosArrowDown } from "react-icons/io";
import ErrorMessageV2 from "@/app/versioning/page/LoginPage/v2/loginComponents/error-message-v2";
import classNames from "classnames";

interface MobileInputBoxV2Props {
  name: string;
  value?: string;
  placeholder?: string;
  isDisabled?: boolean;
  isTouched?: boolean;
  error?: string | false;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  inputContainerClassName?: string;
  labelClassName?: string;
  isShowCustomPrefix?: boolean;
  customPrefix?: (countriesData: Country | undefined) => React.ReactNode;
  onPrefixClick?: () => void;
  isShowSuffix?: boolean;
  suffix?: React.ReactNode;
  selectedCountry: Country | undefined;
  setSelectedCountry: (country: Country) => void;
  initialCountryCode?: string;
  showCountryFlagInPrefix?: boolean;
  styles?: React.CSSProperties;
}

export function MobileInputBoxV2({
  name,
  value,
  placeholder,
  isDisabled = false,
  isTouched = false,
  error = false,
  onChange,
  handleBlur,
  inputContainerClassName = "",
  labelClassName = "",
  isShowCustomPrefix = false,
  customPrefix,
  onPrefixClick,
  isShowSuffix = false,
  suffix,
  selectedCountry,
  setSelectedCountry,
  initialCountryCode = "",
  showCountryFlagInPrefix = false,
  styles,
  ...props
}: MobileInputBoxV2Props) {
  const { code: countryCode, countryISOCode } = selectedCountry || {};

  const [isModalOpen, setIsModalOpen] = useState(false);

  const { data: countriesData } = useCountriesQuery();

  const [focused, setFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const hasValue = Boolean(value ?? inputRef.current?.value);
  const hasError = isTouched && error;

  useEffect(() => {
    if (countriesData) {
      if (initialCountryCode) {
        const initialCountry = countriesData?.find(
          (country) => country.code === initialCountryCode
        );
        if (initialCountry) setSelectedCountry(initialCountry);
      } else if (countriesData.length > 0) {
        setSelectedCountry(countriesData[0]);
      }
    }
  }, [countriesData, initialCountryCode, setSelectedCountry]);

  return (
    <div className="mobile-input-box-v2">
      <div
        className={classNames(
          "mobile-input-container",
          inputContainerClassName,
          {
            error: hasError,
            disabled: isDisabled,
          }
        )}
        style={styles}
      >
        {(isShowCustomPrefix || showCountryFlagInPrefix) && (
          <div
            className={classNames("prefix", { error: hasError })}
            onClick={() => {
              if (!isDisabled) {
                if (onPrefixClick) {
                  onPrefixClick();
                } else {
                  setIsModalOpen(true);
                }
              }
            }}
          >
            {showCountryFlagInPrefix ? (
              <>
                {countryISOCode && (
                  <Image
                    src={`https://flagcdn.com/w40/${countryISOCode}.png`}
                    width={25}
                    height={20}
                    alt=""
                  />
                )}
                <span>{countryCode ?? ""}</span>
                <IoIosArrowDown />
              </>
            ) : (
              customPrefix?.(selectedCountry)
            )}
          </div>
        )}
        <div className="mobile-input__wrapper">
          <div
            className={classNames("mobile-input", {
              "mobile-input--active": focused || hasValue,
            })}
          >
            <div className="mobile-input__inner">
              <label
                className={classNames("mobile-input__label", labelClassName)}
                htmlFor={name}
                onClick={() => {
                  if (!focused && !hasValue) {
                    inputRef.current?.focus();
                  }
                }}
              >
                {placeholder}
              </label>
              <input
                ref={inputRef}
                className="mobile-input__field"
                disabled={isDisabled}
                value={value}
                onFocus={() => setFocused(true)}
                onBlur={(event) => {
                  setFocused(false);
                  handleBlur?.(event as React.FocusEvent<HTMLInputElement>);
                }}
                type="tel"
                name={name}
                inputMode="numeric"
                onBeforeInput={(e) => {
                  if (!/^\d*$/.test(e.data ?? "")) {
                    e.preventDefault();
                  }
                }}
                onChange={onChange}
                {...props}
              />
            </div>
          </div>
        </div>
        {isShowSuffix && <div>{suffix}</div>}
      </div>
      <div className={classNames("error-msg")}>
        <ErrorMessageV2
          message={isTouched && error ? error ?? "" : undefined}
          touched={isTouched}
        />
      </div>
      {isModalOpen && (
        <CountrySelectModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          countriesData={countriesData || []}
          setSelectedCountry={setSelectedCountry}
          selectedCountry={selectedCountry}
        />
      )}
    </div>
  );
}

function CountrySelectModal({
  isOpen,
  onClose,
  countriesData,
  setSelectedCountry,
}: {
  isOpen: boolean;
  onClose: () => void;
  countriesData: Country[];
  setSelectedCountry: (country: Country) => void;
  selectedCountry: Country | undefined;
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const filteredCountries = useMemo(() => {
    if (!searchTerm.trim()) return countriesData;
    return countriesData?.filter((country: Country) =>
      country?.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [searchTerm, countriesData]);
  return (
    <Modal
      show={isOpen}
      onHide={onClose}
      centered
      className="container-country-code-selection-v2"
    >
      <Modal.Header className="w-100 border-0">
        <div className="header">
          <h3>Country Code</h3>
          <div className="close-btn" onClick={onClose}>
            <IoCloseCircle size={24} />
          </div>
        </div>
      </Modal.Header>

      <Modal.Body>
        <div className="search-container">
          <span className="search-icon">
            <Image
              src={"/common-images/search.png"}
              alt={""}
              width={24}
              height={24}
            />
          </span>
          <input
            type="text"
            placeholder="Search for a country"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="country-list">
          {filteredCountries?.map((country, index) => (
            <div
              key={index}
              className="country-item"
              onClick={() => {
                setSelectedCountry(country);
                onClose();
              }}
            >
              <span className="flag">
                <Image
                  src={`https://flagcdn.com/w40/${country?.countryISOCode}.png`}
                  width={25}
                  height={20}
                  alt=""
                  loading="lazy"
                />
              </span>
              <span className="name">{country.name}</span>
              <span className="code">{country.code}</span>
            </div>
          ))}
        </div>
      </Modal.Body>
    </Modal>
  );
}
export default CountrySelectModal;
